import React, { ReactNode } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { SelectValue } from 'antd/lib/select';
import { Select } from 'antd';
import { ICondoUnit, CondoMatchTypeEnum } from 'src/common-types/index';
import { getFullUnitName, getFullUnitNameWithOutId } from 'src/utils/index';
import { ErrorAlertSvg } from 'src/assets/index';
import { Routes } from 'src/constants/index';
import { CondoUnitRow } from '../condo-unit-row';
import './style.scss';

interface IProps extends WrappedComponentProps {
  title: React.ReactNode;
  alternativeUnits: ICondoUnit[];
  condoId: number;
  isOnlyAlternative: boolean;
  currentUnitName: string;
  getCurrentUnit: (value: string, index?: number) => void;
  baseUrl: string;
}

interface IState {
  isAlternativeUnits: boolean;
  isFullUnits: boolean;
}

const ZERO = 0;
const ONE = 1;
const TWO = 2;
const MAX_UNITS_COUNT = 5;

class ListUnitsComponent extends React.Component<IProps, IState> {
  state: IState;

  constructor(props: IProps) {
    super(props);

    let maxCount = ZERO;
    const counts: { [key: string]: number } = {};

    props.alternativeUnits.forEach((unit) => {
      const type = getFullUnitName(unit, props.intl);
      if (counts[type]) {
        counts[type]++;
      } else {
        counts[type] = ONE;
      }
    });

    Object.keys(counts).forEach((key: string) => {
      if (counts[key] > maxCount) {
        maxCount = counts[key];
      }
    });

    this.state = {
      isAlternativeUnits: false,
      isFullUnits: false,
    };
  }

  getAlternativeUnits = (value: SelectValue): ICondoUnit[] => {
    const { intl } = this.props;
    const alternativeUnits = this.props.alternativeUnits
      .filter(
        (item) =>
          getFullUnitName(item, intl) === value &&
          item.matchType === CondoMatchTypeEnum.Alternative,
      )
      .slice();

    return alternativeUnits;
  };

  handleUnitNameChange = (data: { key: string; label: ReactNode; value: string }): void => {
    const { getCurrentUnit } = this.props;

    getCurrentUnit(data.value.toString());
  };

  getNamesUnit = (units: ICondoUnit[]): string[] => {
    const { intl } = this.props;
    return units
      .filter((item, i, unit) => unit.findIndex((currentUnit) => currentUnit.id === item.id) === i)
      .map((item) => {
        return getFullUnitName(item, intl);
      });
  };

  toggleAlternativeUnits = (): void => {
    this.setState(({ isFullUnits }) => ({
      isFullUnits: !isFullUnits,
    }));
  };

  getUnitsToShow = (): ICondoUnit[] => {
    const { currentUnitName } = this.props;
    const { isFullUnits } = this.state;

    return isFullUnits
      ? this.getAlternativeUnits(currentUnitName)
      : this.getAlternativeUnits(currentUnitName).slice(ZERO, MAX_UNITS_COUNT);
  };

  getStyleUnitName = (value: string): React.ReactNode => {
    const name = value.split(' ').splice(ZERO, TWO).join(' ');
    const params = value.replace(name, '');

    return (
      <p>
        <span className="list-units__unit-name bold">{name}</span>
        {params}
      </p>
    );
  };

  render(): React.ReactNode {
    const { title, condoId, isOnlyAlternative, alternativeUnits, currentUnitName, baseUrl } =
      this.props;
    const { isFullUnits } = this.state;

    return (
      <div className="list-units">
        <h3 className="list-units__title">{title}</h3>
        {alternativeUnits.length > ONE ? (
          <Select
            labelInValue
            value={{
              key: currentUnitName,
              label: this.getStyleUnitName(getFullUnitNameWithOutId(currentUnitName)),
              value: currentUnitName,
            }}
            onChange={this.handleUnitNameChange}
          >
            {this.getNamesUnit(alternativeUnits).map((name, index) => (
              <Select.Option key={index} value={name}>
                {getFullUnitNameWithOutId(name)}
              </Select.Option>
            ))}
          </Select>
        ) : null}
        <div className="list-units__wrapper">
          {this.getUnitsToShow().map((unit, index) => (
            <CondoUnitRow
              key={index}
              unit={unit}
              condoId={condoId}
              isOnlyAlternative={isOnlyAlternative}
            />
          ))}
        </div>
        {this.getUnitsToShow().length === 0 ? (
          <div className="list-units__alert-banner">
            <div className="list-units__alert-icon">
              <ErrorAlertSvg />
            </div>
            <div className="list-units__alert-banner-text-wrapper">
              <div className="list-units__alert-banner-header">
                <FormattedMessage id="demand.is.quite.high.for.this.area" />
              </div>
              <div className="list-units__alert-banner-text">
                <FormattedMessage id="request.a.custom.search" />
                {baseUrl ? (
                  <a
                    href={`${Routes.CondoSearch}/submit-request`}
                    className="list-units__support-link"
                  >
                    <FormattedMessage id="click.this.link" />
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
        <div className="list-units__show-link" onClick={this.toggleAlternativeUnits}>
          {this.getAlternativeUnits(currentUnitName).length > MAX_UNITS_COUNT &&
            (isFullUnits ? (
              <FormattedMessage id="show.less" />
            ) : (
              <FormattedMessage id="show.more" />
            ))}
        </div>
      </div>
    );
  }
}

export const ListUnits = injectIntl(ListUnitsComponent);

